import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { APP_ROUTES } from 'constants/routes';
import { useCheckoutSession } from './useCheckoutSession';
import {
  canShowCheckoutInterruptedToast,
  isCheckoutSessionExpired,
} from 'store/checkoutSession';
import { useAppDispatch } from 'store/store';
import {
  addItemToLocalCart,
  clearCartInLocalCart,
  TLocalCartItem,
} from 'store/localCart';
import {
  dismissNotification,
  pushNotification,
} from 'store/notifications/notifications.slices';
import { NOTIFICATION_TYPES } from 'store/notifications/notifications.types';
import { abortStockReservationAsyncThunk } from 'store/stock/stock.action';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const useCheckoutSessionInterruption = () => {
  const dispatch = useAppDispatch();
  const localize = useLocalize();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, clearSession } = useCheckoutSession();
  const shownToastSessionKeyRef = React.useRef<string | null>(null);

  const restoreCartAndClearSession = React.useCallback(() => {
    if (!session) return;

    dispatch(clearCartInLocalCart());
    session.checkoutData.cartItems.forEach((item: TLocalCartItem) => {
      dispatch(addItemToLocalCart(item));
    });
    clearSession();
  }, [clearSession, dispatch, session]);

  React.useEffect(() => {
    if (!session) {
      // Only clear the interrupted-checkout snackbar; do not wipe unrelated toasts.
      if (shownToastSessionKeyRef.current !== null) {
        dispatch(dismissNotification());
      }
      shownToastSessionKeyRef.current = null;
      return;
    }

    if (isCheckoutSessionExpired(session)) {
      dispatch(dismissNotification());
      restoreCartAndClearSession();
      shownToastSessionKeyRef.current = null;
      dispatch(
        pushNotification({
          message: localize('checkout.notification.timeout') as string,
          type: NOTIFICATION_TYPES.WARNING,
        })
      );
      if (location.pathname === APP_ROUTES.CHECKOUT) {
        navigate(APP_ROUTES.HOME);
      }
      return;
    }

    const isOnCheckoutPage = location.pathname === APP_ROUTES.CHECKOUT;
    if (isOnCheckoutPage) {
      // Only dismiss the interrupted-checkout snackbar (with Continue/Abort). Do not clear
      // other notifications (e.g. reservation info from Checkout) while user is on this route.
      if (shownToastSessionKeyRef.current !== null) {
        dispatch(dismissNotification());
      }
      // Allow the interrupted toast again after user is back on checkout;
      // next time they leave checkout, show the message once more.
      shownToastSessionKeyRef.current = null;
      return;
    }

    if (!canShowCheckoutInterruptedToast(session)) {
      dispatch(dismissNotification());
      clearSession();
      shownToastSessionKeyRef.current = null;
      dispatch(
        pushNotification({
          message: localize('checkout.notification.timeout') as string,
          type: NOTIFICATION_TYPES.WARNING,
        })
      );
      return;
    }

    const currentSessionKey = `${session.checkoutStartDate}:${session.checkoutAbortDate}`;
    if (shownToastSessionKeyRef.current === currentSessionKey) {
      return;
    }

    dispatch(
      pushNotification({
        message: localize('checkout.interrupted.message') as string,
        type: NOTIFICATION_TYPES.WARNING,
        snackbarStyle: {
          backgroundColor: '#facc15',
          color: '#1f2937',
        },
        buttonLeft: {
          displayLabel: localize('general.button.continue') as string,
          onClick: () => {
            dispatch(dismissNotification());
            navigate(APP_ROUTES.CHECKOUT);
          },
        },
        buttonRight: {
          displayLabel: localize('general.button.abort') as string,
          onClick: () => {
            dispatch(
              abortStockReservationAsyncThunk({
                body: { id: session.checkoutData.reservationId },
              })
            );
            dispatch(dismissNotification());
            restoreCartAndClearSession();
          },
        },
      })
    );
    shownToastSessionKeyRef.current = currentSessionKey;
  }, [
    clearSession,
    dispatch,
    localize,
    location.pathname,
    navigate,
    restoreCartAndClearSession,
    session,
  ]);
};
